import React from 'react';
import { Box, Typography } from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import ScoreCircularProgressV2 from 'src/common/config/ScoreCircularProgressV2';
import { CustomTooltip } from 'src/content/keywordResearch/CustomTooltip';
import { newCardsCss } from 'src/content/dashboard/DashboardCss';
import { keywordResearchDashBoardCss } from 'src/content/keywordResearch/keywordResearchCSS';
import { KeywordOverviewResultCardsCss } from 'src/content/keywordResearch/keywordOverview/keywordOverviewCSS';

interface RecommendationItem {
  text: string;
  impact: 'High' | 'Medium' | 'Low';
}

interface WeeklySummaryCardProps {
  healthScore?: {
    value: number;
    trend: number;
  };
  newReviews?: number;
  pendingReplies?: number;
  issuesFound?: number;
  completedFixes?: number;
  recommendations?: RecommendationItem[];
}

export default function WeeklySummaryCard({
  healthScore = { value: 0, trend: 0 },
  newReviews = 0,
  pendingReplies = 0,
  issuesFound = 0,
  completedFixes = 0,
  recommendations = []
}: WeeklySummaryCardProps) {

  // Helper to determine impact badge classes
  const getImpactBadgeClass = (impact: 'High' | 'Medium' | 'Low') => {
    switch (impact) {
      case 'High':
        return 'text-[#027A48] bg-[#ECFDF3] px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap';
      case 'Medium':
        return 'text-[#B54708] bg-[#FFFAEB] px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap';
      case 'Low':
      default:
        return 'text-[#344054] bg-[#F2F4F7] px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap';
    }
  };

  return (
    <div className="bg-white border border-[#EAECF0] rounded-xl shadow-sm p-6 w-full">
      {/* Card Header */}
      <div className="flex items-center gap-3 mb-4">
        <Typography sx={newCardsCss?.headTextCss}>
          Weekly Summary
        </Typography>
        <CustomTooltip
          title={
            <Box>
              <Typography
                sx={keywordResearchDashBoardCss?.customTollTipText}
              >
                A summary of your Google Business Profile performance and recommendations over the past 7 days.
              </Typography>
            </Box>
          }
          placement="bottom"
          arrow
        >
          <HelpIcon sx={KeywordOverviewResultCardsCss?.helpIcon} />
        </CustomTooltip>
      </div>

      {/* Top Stats Section */}
      <div className={`flex flex-col lg:flex-row items-center gap-8 pb-6 ${recommendations.length > 0 ? 'border-b border-[#EAECF0]' : ''}`}>
        
        {/* Health Score Progress Circle */}
        <div className="flex-shrink-0 flex items-center justify-center w-full lg:w-fit lg:pr-8 lg:border-r lg:border-[#EAECF0]">
          <ScoreCircularProgressV2
            value={healthScore.value}
            showGradient
          >
              <ScoreCircularProgressV2.Title title='Health Score' className='' />
              <ScoreCircularProgressV2.Value value={healthScore.value} className='' scoreClass='!text-3xl' />
              <ScoreCircularProgressV2.Trend trendValue={healthScore.trend} showPercent={false} className='mt-1' />
          </ScoreCircularProgressV2>
        </div>

        {/* Other Four Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 flex-grow w-full">
          
          {/* New Reviews */}
          <div className="flex flex-col items-center md:items-start md:pl-6 md:border-l border-[#EAECF0] first:border-l-0 first:pl-0">
            <Box className="flex items-center gap-1.5 mb-1">
              <Typography className="!text-sm !text-[#686685] !font-inter !font-normal">
                New Reviews
              </Typography>
              <CustomTooltip
                title={
                  <Box>
                    <Typography
                      sx={keywordResearchDashBoardCss?.customTollTipText}
                    >
                      The number of new reviews left by customers on your profile this week.
                    </Typography>
                  </Box>
                }
                placement="bottom"
                arrow
              >
                <HelpIcon sx={KeywordOverviewResultCardsCss?.helpIcon} />
              </CustomTooltip>
            </Box>
            <span className="text-3xl font-bold text-[#392B65] font-inter mt-1">{newReviews}</span>
          </div>

          {/* Pending Replies */}
          <div className="flex flex-col items-center md:items-start md:pl-6 border-l border-[#EAECF0]">
            <Box className="flex items-center gap-1.5 mb-1">
              <Typography className="!text-sm !text-[#686685] !font-inter !font-normal">
                Pending Replies
              </Typography>
              <CustomTooltip
                title={
                  <Box>
                    <Typography
                      sx={keywordResearchDashBoardCss?.customTollTipText}
                    >
                      The number of reviews you haven't responded to yet.
                    </Typography>
                  </Box>
                }
                placement="bottom"
                arrow
              >
                <HelpIcon sx={KeywordOverviewResultCardsCss?.helpIcon} />
              </CustomTooltip>
            </Box>
            <span className="text-3xl font-bold text-[#392B65] font-inter mt-1">{pendingReplies}</span>
          </div>

          {/* Issues Found */}
          <div className="flex flex-col items-center md:items-start md:pl-6 border-l border-[#EAECF0]">
            <Box className="flex items-center gap-1.5 mb-1">
              <Typography className="!text-sm !text-[#686685] !font-inter !font-normal">
                Issues Found
              </Typography>
              <CustomTooltip
                title={
                  <Box>
                    <Typography
                      sx={keywordResearchDashBoardCss?.customTollTipText}
                    >
                      Issues detected on your profile that may affect your visibility.
                    </Typography>
                  </Box>
                }
                placement="bottom"
                arrow
              >
                <HelpIcon sx={KeywordOverviewResultCardsCss?.helpIcon} />
              </CustomTooltip>
            </Box>
            <span className="text-3xl font-bold text-[#392B65] font-inter mt-1">{issuesFound}</span>
          </div>

          {/* Completed Fixes */}
          <div className="flex flex-col items-center md:items-start md:pl-6 border-l border-[#EAECF0]">
            <Box className="flex items-center gap-1.5 mb-1">
              <Typography className="!text-sm !text-[#686685] !font-inter !font-normal">
                Completed Fixes
              </Typography>
              <CustomTooltip
                title={
                  <Box>
                    <Typography
                      sx={keywordResearchDashBoardCss?.customTollTipText}
                    >
                      Issues and recommendations that you resolved this week.
                    </Typography>
                  </Box>
                }
                placement="bottom"
                arrow
              >
                <HelpIcon sx={KeywordOverviewResultCardsCss?.helpIcon} />
              </CustomTooltip>
            </Box>
            <span className="text-3xl font-bold text-[#392B65] font-inter mt-1">{completedFixes}</span>
          </div>

        </div>
      </div>

      {/* Recommendations / Insights List */}
      {recommendations.length > 0 && (
        <div className="divide-y divide-[#EAECF0]">
          {recommendations.map((item, index) => (
            <div key={index} className="flex justify-between items-center py-2 first:pt-4 last:pb-0">
              <span className="text-base font-semibold text-[#5D5F6C] font-inter flex gap-2">
                <img src="/static/images/icons/ai-visibility/ai.svg" alt="Weeky Summary" width={20} />
                {item.text}
              </span>
              <span className={getImpactBadgeClass(item.impact)}>
                {item.impact} Impact
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
